import MoreGradientIcon from 'ui-components/MoreGradientIcon';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@emotion/react';
import { Box, Grid, CircularProgress, Menu, MenuItem, Switch, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { Add, Search, FilterAltOutlined, FilterAltRounded, MoreVert, ViewKanbanOutlined, AccountTreeOutlined, SettingsOutlined } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { hasPermission, navigateToDomain } from '../../utils';
import { GetInvestorSetting } from 'api/investors';
import SettingsPopup from 'views/deal_flow/popups/SettingsPopup';
import InviteStartupPopup from 'views/startup_applications/popups/InviteStartupPopup';
import DeleteConfirmationDialog from 'ui-components/popups/DeleteConfirmationPopup';
import Cookies from 'js-cookie';
import ThemeBGWrapper from 'ui-components/ThemeBGWrapper';
import BreadcrumbHeader from '../../layout/BreadcrumbsHeader';
import { WHITEBG } from 'ui-components/CustomButton';
import { Primary, Secondary } from 'ui-components/Buttons';
import ModalComponent from 'ui-components/ModalComponent';
import { useToast } from 'context/ToastContext';
import { FetchPipelines, FetchPipelinesStatuses, ChangeStatusForCurrentPipeline } from 'api/startup_applications';
import { SaveFeedback, SaveInterest, FetchInterestFeedbackStatus } from 'api';
import FeedbackPopup from 'views/startup_applications/popups/FeedbackPopup';
import ShowInterestPopup from 'views/startup_applications/popups/ShowInterestPopup';
import PDFView from 'views/startup_applications/popups/PDFView';
import AlertDialog from 'ui-components/popups/AlertPopup';
import { readTabsCache, writeTabsCache } from 'utils/dealFlowTabsCache';
import { BulkUpdateInvestorPipeline, CreatePipeline, CreateDealFlow, ToggleAutoShortlist, InviteStartupPOST, UpdatePipelineFlowName } from 'api/deal_flow';
import { MoveKanbanCards, FetchKanbanColumnIds } from 'api/kanban';
import { hasSelection, selectedCount as countSelection, splitSelection } from './selection';
import { FetchAllStages, FetchAllCategories, FetchAllSubCategories } from 'api/utils';
import FilterPopup from 'views/startup_applications/popups/FilterPopup';
import MoveToPipelineFlowPopup from 'views/deal_flow/popups/MoveToPipelineFlowPopup';
import CreateNewPipelineStatusPopup from 'views/deal_flow/popups/CreateNewPipelineStatusPopup';
import CreateDealFlowPopup from 'views/deal_flow/popups/CreateNewDealFlowPopup';
import AIShortlistPopup from 'views/startup_applications/popups/AIShortlistPopup';
import { SparklesIcon } from 'lucide-react';
import { registerBatchCompletionCallback, unregisterBatchCompletionCallback } from 'components/ProcessingSnackbar';
import { useProcessingSnackbar } from 'context/ProcessingSnackbarContext';
import CustomSwitch from 'ui-components/CustomSwitch';
import KanbanBoard from './components/KanbanBoard';
import SelectionActionBar from 'ui-components/SelectionActionBar';
import { shimmer } from './components/KanbanColumn';
import { StatusOptionIcon } from './components/StatusPill';
import { StatusRail } from 'views/startup_applications/GroupedDealList';
import { CompactSearch, FilterButton, PipelineSwitcher, ViewSwitch } from 'views/startup_applications/DealListToolbar';

// Jobs carry the investor's Bull delay; this is the extra time allowed for the queue
// to drain before the one board reload. Tune here if runs finish later than this.
const AUTO_SHORTLIST_GRACE_MS = 6000;


// BreadcrumbHeader receives `Actions` as an inline arrow, so it gets a fresh component
// type on every parent render and remounts its subtree. If the kebab's anchor element
// lived on the page, opening the menu would re-render the page, detach that anchor node
// from the DOM, and MUI would fall back to positioning the menu at the top-left corner.
// Keeping the anchor state inside this child means opening the menu never re-renders
// the page — exactly how the table view's HeaderActions does it.
const PipelineKebabMenu = ({ pipelineCount = 0, statusCount = 0, onAddPipeline, onManagePipelines, onAddStatus, onManageStatus }) => {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = React.useState(null);
    // Same icon set and muted-white style as the floating SelectionActionBar.
    const itemSx = {
        padding: '0.625rem 1rem',
        gap: '8px',
        color: 'rgba(255, 255, 255, 0.72)',
        '& svg': { fontSize: 18 },
        '&:hover': { backgroundColor: theme.palette.customColors.g500, color: '#fff' },
    };
    const dividerSx = { ...itemSx, borderBottom: `1px solid ${theme.palette.customColors.g700}` };

    return (
        <>
            <WHITEBG
                disableRipple
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{
                    // Borderless: the icon alone is the control. backgroundImage is
                    // neutralised because WHITEBG paints a gradient behind the border area.
                    border: 'none',
                    backgroundImage: 'none',
                    backgroundColor: 'transparent',
                    '&:hover': { backgroundImage: 'none', backgroundColor: 'transparent' },
                    minWidth: 'unset', height: '44.5px', paddingInline: '3px', paddingBlock: '6px',
                    marginLeft: '0.625rem', marginRight: '-0.25rem',
                }}
            >
                <MoreGradientIcon />
            </WHITEBG>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                onClick={() => setAnchorEl(null)}
                PaperProps={{
                    elevation: 0,
                    sx: {
                        overflow: 'visible',
                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                        mt: 1.5, padding: '12px', borderRadius: '8px',
                        '& .MuiList-root': { padding: '0 !important' },
                        bgcolor: theme.palette.customColors.g400,
                        color: theme.palette.customColors.whiteBG,
                        minWidth: '200px',
                    },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                {pipelineCount < 11 && (
                    <MenuItem disableRipple sx={dividerSx} onClick={onAddPipeline}>
                        <Add /> Add Pipeline
                    </MenuItem>
                )}
                {pipelineCount > 0 && (
                    <MenuItem disableRipple sx={dividerSx} onClick={onManagePipelines}>
                        <AccountTreeOutlined /> Manage Pipelines
                    </MenuItem>
                )}
                {statusCount < 8 && (
                    <MenuItem disableRipple sx={dividerSx} onClick={onAddStatus}>
                        <Add /> Add Status
                    </MenuItem>
                )}
                {statusCount > 0 && (
                    <MenuItem disableRipple sx={itemSx} onClick={onManageStatus}>
                        <ViewKanbanOutlined /> Manage Status
                    </MenuItem>
                )}
            </Menu>
        </>
    );
};

// Deal Flow — Kanban board (v2). Isolated page; the existing table page is untouched.
const DealFlowBoard = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { addToast } = useToast();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [investor_id] = React.useState(Cookies.get('X-INV-ID') || null);
    const queryClient = useQueryClient();
    const userPermissions = useSelector((state) => state?.permissions?.userPermissions);
    const userDetails = useSelector((state) => state?.investorDetails?.data);

    // ---- Pipelines (top tabs + "move to pipeline" options) ----
    const { mutateAsync: fetchPipelinesAsync } = FetchPipelines({ investor_id });
    // Seeded from the shared cache (see statusesData below) so the pipeline strip does
    // not blank out when switching views.
    const [pipelinesData, setPipelinesData] = React.useState(() => {
        const cached = readTabsCache(investor_id, 'pipelines');
        return cached ? { data: cached } : null;
    });
    // Active pipeline tab is shared with the table view via the same `pipeline` cookie
    // the table already reads at mount and writes on tab change.
    const [mainTabValue, setMainTabValue] = React.useState(() => {
        try {
            const saved = Cookies.get('pipeline');
            return saved ? JSON.parse(saved) : 'All';
        } catch (e) {
            return 'All';
        }
    });

    const refetchPipelineTabs = React.useCallback((selectId) => {
        fetchPipelinesAsync({ investor_id }, {
            onSuccess: (response) => {
                const mapped = response?.data?.data?.map((item) => ({ ...item, label: item?.name, value: item?._id })) || [];
                setPipelinesData({ data: mapped });
                writeTabsCache(investor_id, 'pipelines', mapped);
                if (selectId) setMainTabValue(selectId);
            },
        });
    }, [investor_id, fetchPipelinesAsync]);

    // ---- Statuses (columns + "move to status" options) ----
    const { mutateAsync: fetchStatusesAsync } = FetchPipelinesStatuses({ investor_id });
    // Seeded from the shared cache so the status strip paints the moment you switch
    // views, instead of blanking out until the re-fetch lands. See utils/dealFlowTabsCache.
    const [statusesData, setStatusesData] = React.useState(() => readTabsCache(investor_id, 'statuses') || []);

    const refetchStatuses = React.useCallback(() => {
        fetchStatusesAsync({ investor_id }, {
            onSuccess: (response) => {
                const mapped = response?.data?.data?.map((item) => ({ ...item, value: item?._id, label: item?.status, mappedValue: item?.value })) || [];
                setStatusesData(mapped);
                writeTabsCache(investor_id, 'statuses', mapped);
            },
        });
    }, [investor_id, fetchStatusesAsync]);

    React.useEffect(() => {
        if (investor_id) { refetchPipelineTabs(); refetchStatuses(); }
    }, [investor_id]);

    const pipelineTabsArray = !pipelinesData?.data
        ? [{ value: 'All', label: 'All', is_fixed: true }]
        : [{ value: 'All', label: 'All', is_fixed: true }, ...pipelinesData.data];

    const handleMainTabChange = (event, newValue) => {
        setMainTabValue(newValue);
        Cookies.set('pipeline', JSON.stringify(newValue), { expires: 1 });
        setSelection({});
    };

    // ---- Search + Filters ----
    // Search is shared with the table view through the SAME `search_startups` cookie the
    // table already reads/writes, so a search survives the List <-> Cards toggle (and
    // clearing it in one view clears it in the other). No change needed on the table side.
    const [searchInput, setSearchInput] = React.useState(() => Cookies.get('search_startups') || '');
    const [search, setSearch] = React.useState(() => Cookies.get('search_startups') || '');
    // Filters ride the same `startups_query` cookie the table already reads/writes.
    const [queryParams, setQueryParams] = React.useState(() => {
        try {
            const saved = Cookies.get('startups_query');
            return saved ? JSON.parse(saved) : {};
        } catch (e) {
            return {};
        }
    });
    const [stage, setStage] = React.useState(null);
    const [category, setCategory] = React.useState(null);
    const [sector, setSector] = React.useState(null);
    const [openFilter, setOpenFilter] = React.useState(false);

    const { data: startup_stages } = FetchAllStages();
    const { data: startup_categories } = FetchAllCategories();
    const { data: startup_sub_categories } = FetchAllSubCategories({ category: queryParams?.category });

    const filterParams = React.useMemo(() => {
        const stageIds = queryParams?.stage
            ? startup_stages?.filter((i) => queryParams.stage.split(',').includes(i?.name)).map((i) => i?._id)
            : null;
        const categoryIds = queryParams?.category
            ? startup_categories?.filter((i) => queryParams.category.split(',').includes(i?.name)).map((i) => i?._id)
            : null;
        const sectorIds = queryParams?.sector
            ? startup_sub_categories?.filter((i) => queryParams.sector.split(',').includes(i?.sub_category)).map((i) => i?._id)
            : null;
        return {
            ...(stageIds?.length ? { select_stages: stageIds.join(',') } : {}),
            ...(categoryIds?.length ? { category: categoryIds.join(',') } : {}),
            ...(sectorIds?.length ? { sub_sector: sectorIds.join(',') } : {}),
            ...(queryParams?.city ? { city: queryParams.city } : {}),
        };
    }, [queryParams, startup_stages, startup_categories, startup_sub_categories]);

    const handleUpdateFilter = ({ query = {} }) => {
        setQueryParams(query);
        Cookies.set('startups_query', JSON.stringify(query));
        setOpenFilter(false);
    };

    const commitSearch = () => {
        const value = searchInput.trim();
        setSearch(value);
        Cookies.set('search_startups', value);
    };

    // Typing: keep the input local, but clearing it applies (and shares) immediately —
    // same as the table, which resets the listing as soon as the box is emptied.
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchInput(value);
        if (value === '') {
            setSearch('');
            Cookies.set('search_startups', '');
        }
    };

    // ---- Selection + reload ----
    // { [statusId]: { all, items: [{ startup_id, pipeline_id, investor_status_id }] } } - see ./selection.js.
    // Selecting is pure state; whole columns are resolved to ids only when a bulk action runs.
    const [selection, setSelection] = React.useState({});
    const [reloadKey, setReloadKey] = React.useState(0);
    const triggerReload = () => setReloadKey((k) => k + 1);

    // Auto-Shortlist runs on a background queue with no completion endpoint. The hook
    // watches it silently and calls back once, at the end, so the board reloads a single
    // time with the final result. Cards only - statuses and pipeline tabs are
    // configuration, and shortlisting never adds or removes a column.
    // Read here rather than with the AI Shortlist block below: the refresh hook's
    // onComplete needs markUploadStepCompleted.
    const { getIsShortlisting, clearCompletedBatches, addBatch, markUploadStepCompleted, removeBatch } = useProcessingSnackbar();

    // Auto-Shortlist queues one background job per invited startup. Rather than poll,
    // wait out the investor's Bull delay plus a grace and reload the board once.
    const autoShortlistBatchRef = React.useRef(null);
    const autoShortlistTimerRef = React.useRef(null);
    const [isAutoShortlisting, setIsAutoShortlisting] = React.useState(false);
    // Per-column counts reported up by KanbanBoard - saves re-asking the API.
    const [boardColumns, setBoardColumns] = React.useState(null);

    const clearAutoShortlistTimer = React.useCallback(() => {
        if (autoShortlistTimerRef.current) {
            clearTimeout(autoShortlistTimerRef.current);
            autoShortlistTimerRef.current = null;
        }
    }, []);

    React.useEffect(() => clearAutoShortlistTimer, [clearAutoShortlistTimer]);

    // Opens the same snackbar the AI Shortlist uses; the timer below closes it.
    const startAutoShortlistWithSnackbar = React.useCallback((delayMs) => {
        const batchId = `auto-shortlist-${Date.now()}`;
        autoShortlistBatchRef.current = batchId;
        addBatch(batchId, {
            batchType: 'auto_shortlist',
            uploadStep: 'processing',
            uploadedFileNames: [],
            notifyOnComplete: false,
        });
        setIsAutoShortlisting(true);
        clearAutoShortlistTimer();
        autoShortlistTimerRef.current = setTimeout(() => {
            autoShortlistTimerRef.current = null;
            setIsAutoShortlisting(false);
            if (autoShortlistBatchRef.current) {
                markUploadStepCompleted(autoShortlistBatchRef.current);
                autoShortlistBatchRef.current = null;
            }
            triggerReload();
        }, Math.max(0, Number(delayMs) || 0) + AUTO_SHORTLIST_GRACE_MS);
    }, [addBatch, markUploadStepCompleted, clearAutoShortlistTimer]);

    const clearSelection = React.useCallback(() => setSelection({}), []);

    const selectedCount = React.useMemo(() => countSelection(selection, boardColumns), [selection, boardColumns]);

    // Selected cards as [{ startup_id, pipeline_id, investor_status_id }]: explicit picks, plus
    // one ids-only call per wholly selected column (minus the cards unticked in it).
    const { mutateAsync: fetchIdsAsync } = FetchKanbanColumnIds();
    const resolveSelection = async () => {
        const { picks, scopes } = splitSelection(selection);
        const lists = await Promise.all(scopes.map(async (scope) => {
            const res = await fetchIdsAsync({ investor_id, investor_status_id: scope.investor_status_id, investor_pipeline_id: mainTabValue, name: search, ...filterParams });
            if (res?.data?.statusCode !== 200) throw new Error(res?.data?.message);
            if (res.data.data?.truncated) addToast(`Only the first ${res.data.data.items.length} startups of a column can be acted on at once.`, 'warning');
            return (res.data.data?.items || []).filter((i) => !scope.exclude.has(String(i.startup_id)));
        }));
        return [...picks, ...lists.flat()];
    };

    // ---- Bulk actions ----
    const { mutateAsync: bulkPipelineAsync, isLoading: isBulkPipeline } = BulkUpdateInvestorPipeline();
    const { mutateAsync: moveCardsAsync } = MoveKanbanCards();
    const [isBulkStatus, setIsBulkStatus] = React.useState(false);

    // ---- AI Shortlist (reuses the table's AIShortlistPopup + processing snackbar) ----
    const isShortlisting = React.useMemo(() => getIsShortlisting(), [getIsShortlisting]);
    const [isAIShortlistProcessing, setIsAIShortlistProcessing] = React.useState(false);
    const [openAIShortlistPopup, setOpenAIShortlistPopup] = React.useState(false);

    // Startup ids the popup acts on, resolved when it opens.
    const [aiSelectedIds, setAiSelectedIds] = React.useState(() => new Set());

    const handleAIShortlistPopupOpen = async () => {
        if (!hasSelection(selection)) { addToast('Select Startups Before Beginning', 'warning'); return; }
        try {
            const items = await resolveSelection();
            setAiSelectedIds(new Set(items.map((i) => String(i.startup_id))));
        } catch (e) {
            addToast('Could not prepare the selected startups. Please try again.', 'error');
            return;
        }
        clearCompletedBatches();
        setOpenAIShortlistPopup(true);
    };

    React.useEffect(() => {
        const onComplete = () => { setIsAIShortlistProcessing(false); setSelection({}); triggerReload(); };
        registerBatchCompletionCallback('dealflow_shortlist', onComplete);
        return () => unregisterBatchCompletionCallback('dealflow_shortlist');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Move to Status: one /kanban/move-many call for all selected cards (any column).
    const handleBulkStatus = async (props) => {
        const target = statusesData.find((i) => i?.value === props?.status_value);
        setIsBulkStatus(true);
        try {
            const cards = (await resolveSelection()).filter((c) => String(c.investor_status_id) !== String(props?.status_value));
            if (!cards.length) { addToast('Selected startups are already in that status.', 'error'); return; }
            const res = await moveCardsAsync({ pipeline_ids: cards.map((c) => c.pipeline_id), investor_status_id: props?.status_value, status: target?.mappedValue });
            if (res?.data?.statusCode !== 200) throw new Error(res?.data?.message);
            addToast(`Selected startups moved to ${target?.label} (Status)`, 'success');
            setSelection({}); triggerReload();
        } catch (e) {
            addToast('Could not move some startups. Please try again.', 'error');
        } finally {
            setIsBulkStatus(false);
        }
    };

    // Move to Pipeline: single bulk call (startup ids), no emails.
    const handleBulkPipeline = async (props) => {
        const selected = pipelineTabsArray.find((i) => i?.value === props?.status_value);
        let items;
        try {
            items = await resolveSelection();
        } catch (e) {
            addToast('Could not prepare the selected startups. Please try again.', 'error');
            return;
        }
        const payload = {
            investor_id,
            investor_pipeline_id: props?.status_value,
            selected_ids: items.map((i) => String(i.startup_id)),
            select_all: false,
            ...(mainTabValue !== 'All' ? { current_investor_pipeline_id: mainTabValue } : {}),
        };
        bulkPipelineAsync(payload, {
            onSuccess: (res) => {
                if (res?.data?.success === false || res?.data?.modifiedCount === 0) { addToast('No startups were updated.', 'error'); return; }
                addToast(`Selected startups moved to ${selected?.label} (Pipeline)`, 'success');
                setSelection({}); triggerReload();
            },
            onError: (e) => addToast(e?.response?.data?.message || 'Could not move startups.', 'error'),
        });
    };

    // ---- Card ⋯ menu: the same startup actions as the list row menu ----
    // Popups, API calls and payloads mirror views/startup_applications so both views behave alike.
    const [cardMenu, setCardMenu] = React.useState(null); // { anchorEl, card }
    const [actionRow, setActionRow] = React.useState(null); // row shape the list popups expect
    const [openFeedback, setOpenFeedback] = React.useState(false);
    const [openInterest, setOpenInterest] = React.useState(false);
    const [openCardPipeline, setOpenCardPipeline] = React.useState(false);
    const [pdfUrl, setPdfUrl] = React.useState(null);
    const [alertInfo, setAlertInfo] = React.useState(null); // { heading, message }
    const { mutateAsync: fetchFeedbackStatusAsync } = FetchInterestFeedbackStatus();
    const { mutateAsync: saveFeedbackAsync } = SaveFeedback();
    const { mutateAsync: saveInterestAsync } = SaveInterest();
    const { mutateAsync: changePipelineAsync, isLoading: isChangingPipeline } = ChangeStatusForCurrentPipeline();

    const toActionRow = (card) => ({
        _id: card.startup_id,
        startup_name: card.startup_name,
        pipeline_id: card.pipeline_id,
        pipeline_status: statusesData?.find((s) => String(s?.value) === String(card.investor_status_id))?.mappedValue,
        pitchDeckFileUrl: card.pitchDeckFileUrl,
    });
    const openCardMenu = React.useCallback((card, anchorEl) => setCardMenu({ card, anchorEl }), []);
    const closeCardMenu = () => setCardMenu(null);

    const handleCardFeedback = (card) => {
        const row = toActionRow(card);
        setActionRow(row);
        // Same check as the list: one feedback per startup.
        fetchFeedbackStatusAsync({ startup_id: row._id, investor_id }).then((response) => {
            if (response?.data?.feedback !== null) {
                setAlertInfo({ heading: 'Feedback', message: `You have already submitted feedback for ${row.startup_name}. Please wait to hear back from the startup.` });
            } else {
                setOpenFeedback(true);
            }
        }).catch((err) => addToast(err?.response?.data?.message, 'error'));
    };
    const handleFeedbackSubmit = ({ row, values }) => {
        saveFeedbackAsync({
            startup_id: row?._id,
            investor_id,
            ratings: { problem: values?.problem, market: values?.market, product: values?.product, team: values?.team, traction: values?.traction },
            investible: values?.invest,
            feedback: values?.feedback,
        }).then(() => triggerReload());
        setOpenFeedback(false);
    };
    const handleInterestSubmit = ({ row, values }) => {
        saveInterestAsync({
            startup_id: row?._id,
            interested_amount: Number(String(values?.interested_amount)?.replaceAll(',', '')) || 0,
            investor_id,
            currency_id: values?.currency_id,
            ...(row?.pipeline_status === 'in-progress' ? { status: 'in-progress' } : {}),
        }).then(() => triggerReload());
        setOpenInterest(false);
    };
    const handleCardPipelineMove = (props) => {
        changePipelineAsync({ payload: { investor_pipeline_id: props?.status_value }, pipeline_id: props?.pipeline_id }, {
            onSuccess: (res) => {
                if (res?.data?.statusCode === 200) { setOpenCardPipeline(false); triggerReload(); }
            },
            onError: (err) => addToast(err?.response?.data?.message, 'error'),
        });
    };
    // `target` is set by the card's quick-action chips; the ⋯ menu passes nothing and uses its own card.
    const runCardAction = (action, target) => {
        const card = target || cardMenu?.card;
        closeCardMenu();
        if (!card) return;
        if (action === 'interest') { setActionRow(toActionRow(card)); setOpenInterest(true); }
        if (action === 'feedback') handleCardFeedback(card);
        if (action === 'deck') { setActionRow(toActionRow(card)); setPdfUrl(card.pitchDeckFileUrl); }
        if (action === 'email') window.location.href = `mailto:${card.email}`;
        if (action === 'pipeline') { setActionRow(toActionRow(card)); setOpenCardPipeline(true); }
        if (action === 'profile') window.open(navigateToDomain('main', `startup/${card.slug}`));
        if (action === 'details') window.open(`/deal-flow/view-profile?startup_id=${card.startup_id}`);
        // Quick-action chips - same targets as the list row's icons.
        if (action === 'website') window.open(card.website_url);
        if (action === 'linkedin') window.open(card.linkedin_url);
        if (action === 'whatsapp') {
            const phone = String(card.phone || '').replace(/[^\d]/g, '');
            const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) && window.innerWidth < 768;
            window.open(isMobile ? `https://wa.me/${phone}` : `https://web.whatsapp.com/send?phone=${phone}`, '_blank', 'noopener');
        }
        if (action === 'round') { Cookies.set('startup_id', card.startup_id); navigate('/deal-flow/view-round'); }
    };
    // Stable for the memoised cards; always runs the latest runCardAction.
    const runCardActionRef = React.useRef(runCardAction);
    runCardActionRef.current = runCardAction;
    const handleCardAction = React.useCallback((action, card) => runCardActionRef.current(action, card), []);
    const cardMenuItemSx = { padding: '0.625rem 1rem', '&:hover': { backgroundColor: theme.palette.customColors.g500 } };

    // ---- Create pipeline / status ----
    const { mutateAsync: createPipelineAsync, isLoading: isCreatingPipeline } = CreatePipeline({ investor_id });
    const { mutateAsync: createStatusAsync, isLoading: isCreatingStatus } = CreateDealFlow();
    const [openCreatePipeline, setOpenCreatePipeline] = React.useState(false);
    const [openCreateStatus, setOpenCreateStatus] = React.useState(false);

    const handleCreatePipeline = ({ payload }) => {
        createPipelineAsync({ ...payload }, {
            onSuccess: (response) => {
                if (response?.data?.statusCode === 201) { refetchPipelineTabs(response?.data?.data?._id); setOpenCreatePipeline(false); }
                else if (response?.data?.statusCode === 409) { addToast('A pipeline with that name already exists. Enter a unique name.', 'error'); }
                else { addToast(response?.data?.message || 'Could not create pipeline.', 'error'); }
            },
        });
    };

    const handleCreateStatus = (props) => {
        createStatusAsync({ investorId: investor_id, newStatus: props?.status }, {
            onSuccess: (response) => {
                // The board adds the new status as an empty column from the refreshed list; no reload.
                if (response?.data?.statusCode === 200) { refetchStatuses(); setOpenCreateStatus(false); }
                else if (response?.data?.statusCode === 409) { addToast('A status with that name already exists. Enter a unique name.', 'error'); }
                else { addToast(response?.data?.message || 'Could not create status.', 'error'); }
            },
        });
    };

    // ---- Auto-shortlist toggle (reuses the table's setting + toggle APIs) ----
    const { data: fetchedSettingFromBackend, isLoading: isFetchingSetting } = GetInvestorSetting({ investor_id, module: 'deal_flow' });
    const { mutateAsync: toggleAutoShortlistAsync } = ToggleAutoShortlist();
    const [autoShortlistEnabled, setAutoShortlistEnabled] = React.useState(false);
    const [autoShortlistConfirmDialog, setAutoShortlistConfirmDialog] = React.useState(false);
    const [pendingAutoShortlistState, setPendingAutoShortlistState] = React.useState(null);
    React.useEffect(() => {
        if (fetchedSettingFromBackend?.auto_shortlist) setAutoShortlistEnabled(fetchedSettingFromBackend.auto_shortlist.enabled || false);
    }, [fetchedSettingFromBackend]);

    // ---- Invite Startup ----
    const { mutateAsync: inviteStartupAsync, isLoading: isInvitingStartup } = InviteStartupPOST({ investor_id });
    const [openInviteStartupPopup, setOpenInviteStartupPopup] = React.useState(false);
    const handleInviteStartup = async ({ payload }) => {
        await inviteStartupAsync({ payload, _id: investor_id }).then((response) => {
            if (response?.data?.statusCode === 200) { addToast('Invitation sent successfully', 'success'); setOpenInviteStartupPopup(false); triggerReload(); }
            else if (response?.data?.statusCode === 409) { addToast('Startup is already in your deal flow', 'warning'); }
            else { addToast('Something went wrong. Please try again', 'error'); }
        }).catch(() => addToast('Failed to send invitation. Please check and try again', 'error'));
    };

    // ---- Manage Pipelines / Status (SettingsPopup) ----
    const { mutateAsync: updatePipelineFlow } = UpdatePipelineFlowName();
    const [openSettingsModal, setOpenSettingsModal] = React.useState(false);
    const [settingsType, setSettingsType] = React.useState(null);
    const [value, setValue] = React.useState(null);
    const handleOpenPipelineFlowSettingsModal = () => { setSettingsType('pipeline'); setOpenSettingsModal(true); };
    const handleOpenDealFlowSettingsModal = () => { setSettingsType('status'); setOpenSettingsModal(true); };
    const handleCloseSettingsModal = () => { setOpenSettingsModal(false); setSettingsType(null); };
    const handleReorderPipelines = (items) => setPipelinesData({ data: items });
    // Called after the new order is saved; the board re-sorts its columns from statusesData, no reload.
    const handleReorderStatuses = (items) => setStatusesData(items);
    const handleDeletePipeline = (items) => {
        const filtered = items?.filter((el) => el.value !== 'All');
        setPipelinesData({ data: filtered });
        setMainTabValue(filtered?.[0]?._id || 'All');
    };
    // Only an empty status can be deleted, so the board just drops its column; no reload.
    const handleDeleteStatus = (items) => setStatusesData(items);
    const onEditStatus = (props) => {
        if (props?.label !== statusesData?.find((item) => item?.value === props?.deal_flow_id)?.label)
            createStatusAsync({ investorId: investor_id, statusId: props?.status_id, newStatus: props?.label }, {
                onSuccess: () => {
                    setStatusesData(statusesData.map((item) => (item?.value === props?.status_id ? { ...item, label: props?.label, status: props?.label } : item)).sort((a, b) => a?.display_order - b?.display_order));
                },
            });
    };
    // Saved color/icon lands on the board's columns at once; the picker has already sent it to the API.
    const onChangeStatusAppearance = ({ status_id, ...fields }) => {
        const next = statusesData.map((item) => (item?.value === status_id ? { ...item, ...fields } : item));
        setStatusesData(next);
        writeTabsCache(investor_id, 'statuses', next);
    };
    const onEditPipeline = (props) => {
        if (props?.label !== pipelineTabsArray?.find((item) => item?.value === props?.pipeline_id)?.label)
            updatePipelineFlow({ pipeline_id: props?.pipeline_id, investor_id, payload: { name: props?.label } }, {
                onSuccess: () => {
                    setPipelinesData({ data: pipelineTabsArray?.filter((item) => item?.value !== 'All').map((item) => (item?.value === props?.pipeline_id ? { ...item, label: props?.label, name: props?.label } : item)).sort((a, b) => a?.display_order - b?.display_order) });
                },
            });
    };

    return (
        <ThemeBGWrapper>
            {/* Suppress BreadcrumbHeader's built-in divider — the design places the divider below the controls row. */}
            <BreadcrumbHeader
                title={'Pipelines'}
                breadcrumbTitle="Pipelines"
                Actions={() => (
                    <Box sx={{ display: 'flex' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
                            {/* Auto-shortlist toggle — same behaviour as the table header */}
                            <Tooltip title="Auto-Shortlist startups based on your Investment Thesis" placement="bottom" arrow>
                                <Box sx={{ display: 'flex', alignItems: 'center', mr: 1.5 }}>
                                    <Typography sx={{ display: { xs: 'none', md: 'block' }, fontSize: '0.85rem', fontWeight: 600, whiteSpace: 'nowrap', color: theme.palette.customColors.g500 }}>
                                        Auto-Shortlist
                                    </Typography>
                                    {/* Until the setting arrives the switch would render OFF and then flip —
                                        show a shimmer track instead, sized like the real CustomSwitch
                                        (38x20 plus its ml:1), so nothing shifts when it lands. */}
                                    {isFetchingSetting ? (
                                        <Box sx={{ ml: 1, width: 38, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Box sx={{ ...shimmer, width: 38, height: 20, borderRadius: '999px' }} />
                                        </Box>
                                    ) : (
                                        <CustomSwitch
                                            // Previews the pending state while the confirm dialog is open.
                                            checked={autoShortlistConfirmDialog && pendingAutoShortlistState !== null
                                                ? pendingAutoShortlistState
                                                : autoShortlistEnabled}
                                            disabled={isAutoShortlisting}
                                            onChange={(e) => {
                                                const newEnabled = e.target.checked;
                                                const hasRequiredFields =
                                                    fetchedSettingFromBackend?.investment_thesis?.selected_status_id &&
                                                    fetchedSettingFromBackend?.investment_thesis?.rejected_status_id &&
                                                    fetchedSettingFromBackend?.investment_thesis?.stage;
                                                if (newEnabled && !hasRequiredFields) {
                                                    addToast(
                                                        'Configure your investment thesis to use auto-shortlist',
                                                        'warning',
                                                        5000,
                                                        { label: 'Configure', href: '/settings?page=investment-thesis', onClick: () => navigate('/settings?page=investment-thesis') }
                                                    );
                                                    return;
                                                }
                                                setPendingAutoShortlistState(newEnabled);
                                                setAutoShortlistConfirmDialog(true);
                                            }}
                                            sx={{ ml: 1 }}
                                        />
                                    )}
                                </Box>
                            </Tooltip>

                            {/* Invite Startup — permissions arrive from redux (null at first paint), so
                                the button would pop in. Render a shimmer with the SAME content (made
                                transparent) while they load, which reserves the exact width/height. */}
                            {userPermissions === null ? (
                                <Box
                                    aria-hidden
                                    sx={{
                                        ...shimmer,
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        height: 42, borderRadius: '10px', flexShrink: 0,
                                        color: 'transparent', userSelect: 'none', pointerEvents: 'none',
                                        // Mirrors the real button's responsive geometry below, so the
                                        // reserved space matches whichever form actually renders.
                                        ...(isSmallScreen
                                            ? { width: 42, minWidth: 42, padding: 0, gap: 0 }
                                            : { width: 'auto', padding: '0 16px', gap: '6px', fontWeight: 600, fontSize: '14px', lineHeight: 1 }),
                                    }}
                                >
                                    <Add sx={{ fontSize: 20, color: 'transparent' }} />
                                    {!isSmallScreen && 'Invite Startup'}
                                </Box>
                            ) : hasPermission('startups', 'invite', userPermissions) && (
                                <Primary
                                    onClick={() => setOpenInviteStartupPopup(true)}
                                    sx={{
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        height: 42, borderRadius: '10px', flexShrink: 0,
                                        // Below sm the label is dropped for a 42x42 icon button, matching the
                                        // list view's Actions (isSmUp/showLabel + actionBtnSx). Keeping the
                                        // full label wrapped "Invite Startup" onto two lines on a phone.
                                        ...(isSmallScreen
                                            ? { width: 42, minWidth: 42, padding: 0, gap: 0 }
                                            : { width: 'auto', padding: '0 16px', gap: '6px', fontWeight: 600, fontSize: '14px', lineHeight: 1 }),
                                    }}
                                >
                                    <Add sx={{ fontSize: 20, flexShrink: 0 }} />
                                    {!isSmallScreen && 'Invite Startup'}
                                </Primary>
                            )}
                        </Box>

                        {/* Kebab — pipeline / status management. Owns its own anchor state
                            (see PipelineKebabMenu) so opening it never re-renders this page. */}
                        <PipelineKebabMenu
                            pipelineCount={pipelinesData?.data?.length || 0}
                            statusCount={statusesData?.length || 0}
                            onAddPipeline={() => setOpenCreatePipeline(true)}
                            onManagePipelines={handleOpenPipelineFlowSettingsModal}
                            onAddStatus={() => setOpenCreateStatus(true)}
                            onManageStatus={handleOpenDealFlowSettingsModal}
                        />
                    </Box>
                )}
                sx={{
                    // No maxHeight:'unset' on the flex-wrap: keeping BreadcrumbHeader's default
                    // maxHeight:30 makes this header the SAME small height as the list view and
                    // Reels. The tall controls (Invite Startup / kebab) overflow the 30px row
                    // into the padding, exactly like the list header does.
                    paddingBottom: '0 !important',
                }}
            />

            <Grid container xs={12} sx={{ paddingInline: { xs: '0.8rem', sm: '1rem', md: '2rem', lg: '2rem' }, paddingBottom: '1rem' }}>
                {/* Same card and header bar as the grouped list (its rail without the status tabs), so
                    List <-> Board only swaps the content. overflow: hidden clips the columns' sideways scroll. */}
                <Grid item xs={12}>
                    <Box
                        sx={{
                            bgcolor: 'white',
                            border: '1px solid #EAECF0',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            mt: '16px',
                        }}
                    >
                        <StatusRail
                            sx={{ position: 'relative', top: 'auto', width: '100%' }}
                            railStart={
                                <PipelineSwitcher
                                    pipelines={pipelineTabsArray}
                                    value={mainTabValue}
                                    onChange={(next) => handleMainTabChange(null, next)}
                                    onAddPipeline={() => setOpenCreatePipeline(true)}
                                    canAddPipeline={pipelineTabsArray?.length < 11}
                                    onManagePipelines={pipelineTabsArray?.length > 1 ? handleOpenPipelineFlowSettingsModal : undefined}
                                />
                            }
                            railEnd={<>
                                <CompactSearch
                                    value={searchInput}
                                    onChange={(next) => handleSearchChange({ target: { value: next } })}
                                    onSubmit={commitSearch}
                                    onClear={() => handleSearchChange({ target: { value: '' } })}
                                />
                                <FilterButton activeCount={Object.keys(queryParams || {}).length} onClick={() => setOpenFilter(true)} />
                            </>}
                            railTrailing={<ViewSwitch />}
                        />
                        {/* Breathing room so the columns do not touch the card edges; smaller on phones (320px columns). */}
                        <Box sx={{ padding: { xs: '0.5rem', sm: '1rem' } }}>
                        {/* Floating bulk actions while startups are selected (shared with the list view). */}
                        <SelectionActionBar
                            count={selectedCount}
                            onClear={clearSelection}
                            actions={[
                                {
                                    key: 'status', label: 'Status', icon: <ViewKanbanOutlined />, loading: isBulkStatus,
                                    menu: {
                                        title: 'Statuses', searchPlaceholder: 'Search statuses...', optionIcon: <ViewKanbanOutlined />,
                                        options: statusesData.map((item) => ({ ...item, icon: <StatusOptionIcon status={item} /> })),
                                        onSelect: (option) => handleBulkStatus({ status_value: option.value }),
                                        footer: [
                                            (statusesData?.length || 0) < 8 && { key: 'add', label: 'Add Status', icon: <Add />, onClick: () => setOpenCreateStatus(true) },
                                            (statusesData?.length || 0) > 0 && { key: 'manage', label: 'Manage Status', icon: <SettingsOutlined />, onClick: handleOpenDealFlowSettingsModal },
                                        ],
                                    },
                                },
                                ...(pipelineTabsArray?.length > 1
                                    ? [{
                                        key: 'pipeline', label: 'Pipeline', icon: <AccountTreeOutlined />, loading: isBulkPipeline,
                                        menu: {
                                            title: 'Pipelines', searchPlaceholder: 'Search pipelines...', optionIcon: <AccountTreeOutlined />,
                                            options: pipelineTabsArray?.filter((item) => item?.value !== mainTabValue),
                                            onSelect: (option) => handleBulkPipeline({ status_value: option.value }),
                                            footer: [
                                                (pipelinesData?.data?.length || 0) < 11 && { key: 'add', label: 'Add Pipeline', icon: <Add />, onClick: () => setOpenCreatePipeline(true) },
                                                (pipelinesData?.data?.length || 0) > 0 && { key: 'manage', label: 'Manage Pipelines', icon: <SettingsOutlined />, onClick: handleOpenPipelineFlowSettingsModal },
                                            ],
                                        },
                                    }]
                                    : []),
                                {
                                    key: 'ai-shortlist', label: 'AI Shortlist', icon: <SparklesIcon width={16} height={16} />, highlight: true,
                                    onClick: handleAIShortlistPopupOpen,
                                    disabled: isAIShortlistProcessing || isShortlisting,
                                    loading: isAIShortlistProcessing || isShortlisting,
                                },
                            ]}
                        />
                        <KanbanBoard
                            investor_id={investor_id}
                            pipelineId={mainTabValue}
                            search={search}
                            filterParams={filterParams}
                            selection={selection}
                            onSelectionChange={setSelection}
                            onClearSelection={clearSelection}
                            reloadKey={reloadKey}
                            statuses={statusesData}
                            customizeEmail={fetchedSettingFromBackend?.customize_email}
                            onColumnsChange={setBoardColumns}
                            onAddStatus={() => setOpenCreateStatus(true)}
                            onOpenCardMenu={openCardMenu}
                            onCardAction={handleCardAction}
                            onManageStatus={(statusesData?.length || 0) > 0 ? handleOpenDealFlowSettingsModal : undefined}
                            onInviteStartup={hasPermission('startups', 'invite', userPermissions) ? () => setOpenInviteStartupPopup(true) : undefined}
                        />
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            {/* Card ⋯ menu - same items as the list row menu (deck / message are chips on the card) */}
            <Menu
                anchorEl={cardMenu?.anchorEl}
                open={Boolean(cardMenu)}
                onClose={closeCardMenu}
                PaperProps={{
                    elevation: 0,
                    sx: {
                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))', padding: '8px', borderRadius: '8px',
                        '& .MuiList-root': { padding: '0 !important' },
                        bgcolor: theme.palette.customColors.g400, color: theme.palette.customColors.whiteBG, minWidth: '190px',
                    },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <MenuItem disableRipple sx={cardMenuItemSx} onClick={() => runCardAction('interest')}>Submit Interest</MenuItem>
                <MenuItem disableRipple sx={cardMenuItemSx} onClick={() => runCardAction('feedback')}>Submit Feedback</MenuItem>
                {pipelineTabsArray?.length > 1 && <MenuItem disableRipple sx={cardMenuItemSx} onClick={() => runCardAction('pipeline')}>Move to Pipeline</MenuItem>}
                {cardMenu?.card?.slug && <MenuItem disableRipple sx={cardMenuItemSx} onClick={() => runCardAction('profile')}>View Profile</MenuItem>}
                <MenuItem disableRipple sx={cardMenuItemSx} onClick={() => runCardAction('details')}>View Details</MenuItem>
            </Menu>
            <ModalComponent open={openFeedback} onClose={() => setOpenFeedback(false)} style={{ maxWidth: 640, width: '100%' }}>
                <FeedbackPopup currentRow={actionRow} handleClose={() => setOpenFeedback(false)} handleSubmit={handleFeedbackSubmit} />
            </ModalComponent>
            <ModalComponent open={openInterest} onClose={() => setOpenInterest(false)} style={{ maxWidth: 640, width: '100%' }}>
                <ShowInterestPopup currentRow={actionRow} handleClose={() => setOpenInterest(false)} handleSubmit={handleInterestSubmit} />
            </ModalComponent>
            <PDFView
                open={Boolean(pdfUrl)}
                pdfUrl={pdfUrl}
                currentRow={actionRow}
                handleClose={() => setPdfUrl(null)}
                handleFeedbackPopupOpen={() => { setPdfUrl(null); setOpenFeedback(true); }}
                handleShowInterestPopupOpen={() => { setPdfUrl(null); setOpenInterest(true); }}
            />
            {openCardPipeline && (
                <MoveToPipelineFlowPopup
                    open={openCardPipeline}
                    handleClose={() => setOpenCardPipeline(false)}
                    handleSubmission={handleCardPipelineMove}
                    isCreating={isChangingPipeline}
                    options={pipelineTabsArray?.filter((item) => item?.value !== 'All' && item?.value !== mainTabValue)}
                    currentPipelineID={actionRow?.pipeline_id}
                />
            )}
            <AlertDialog open={Boolean(alertInfo)} handleClose={() => setAlertInfo(null)} heading={alertInfo?.heading} subheading={alertInfo?.message} />

            {/* Filter */}
            <ModalComponent open={openFilter} onClose={() => setOpenFilter(false)} style={{ maxWidth: 640, width: '100%' }}>
                <FilterPopup
                    queryParams={queryParams}
                    setQueryParams={setQueryParams}
                    handleClose={() => setOpenFilter(false)}
                    handleSubmit={handleUpdateFilter}
                    stage={stage} category={category} sector={sector}
                    setStage={setStage} setCategory={setCategory} setSector={setSector}
                    uniqueCountryCity={[]}
                />
            </ModalComponent>

            {/* Create pipeline / status */}
            {openCreatePipeline && (
                <CreateNewPipelineStatusPopup open={openCreatePipeline} handleClose={() => setOpenCreatePipeline(false)} handleSubmission={handleCreatePipeline} isCreating={isCreatingPipeline} />
            )}
            {openCreateStatus && (
                <CreateDealFlowPopup open={openCreateStatus} handleClose={() => setOpenCreateStatus(false)} handleSubmission={handleCreateStatus} isCreating={isCreatingStatus} />
            )}

            {/* AI Shortlist (reuses the table's popup) */}
            <AIShortlistPopup
                open={openAIShortlistPopup}
                handleClose={() => setOpenAIShortlistPopup(false)}
                investor_id={investor_id}
                selectedIds={aiSelectedIds}
                selectAll={false}
                selectedCount={aiSelectedIds.size}
                activeFilters={{
                    category: filterParams.category ? filterParams.category.split(',') : [],
                    sector: filterParams.sub_sector ? filterParams.sub_sector.split(',') : [],
                    stage: filterParams.select_stages ? filterParams.select_stages.split(',') : [],
                    name: search || '',
                }}
                investorPipelineId={mainTabValue !== 'All' ? mainTabValue : undefined}
                investorStatusId={undefined}
                refetchListing={triggerReload}
                onProcessingChange={setIsAIShortlistProcessing}
            />

            {/* Invite Startup (reuses the table's popup) */}
            {openInviteStartupPopup && (
                <InviteStartupPopup
                    open={openInviteStartupPopup}
                    handleClose={() => setOpenInviteStartupPopup(false)}
                    isSharingDocuments={false}
                    refetchListing={triggerReload}
                    handleSubmission={handleInviteStartup}
                    isInvitingStartup={isInvitingStartup}
                    affiliate_code={userDetails?.affiliate_code}
                    slug={pipelineTabsArray?.find((p) => p?.value === mainTabValue)?.slug}
                    pipeline_id={mainTabValue}
                />
            )}

            {/* Manage Pipelines / Status (reuses the table's SettingsPopup) */}
            {openSettingsModal && (
                <SettingsPopup
                    open={openSettingsModal}
                    handleClose={handleCloseSettingsModal}
                    mainTabValue={mainTabValue}
                    setMainTabValue={setMainTabValue}
                    tabValue={value}
                    setValue={setValue}
                    settingsType={settingsType}
                    pipelineTabs={pipelineTabsArray}
                    statusTabs={statusesData}
                    investor_id={investor_id}
                    handleReorderPipelines={handleReorderPipelines}
                    handleReorderStatuses={handleReorderStatuses}
                    onEditStatus={onEditStatus}
                    onChangeStatusAppearance={onChangeStatusAppearance}
                    onEditPipeline={onEditPipeline}
                    handleDeletePipeline={handleDeletePipeline}
                    handleDeleteStatus={handleDeleteStatus}
                    refetchPipelineTabs={refetchPipelineTabs}
                    refetchStatusTabs={refetchStatuses}
                    sx={{ '& > .MuiDialog-container > .MuiPaper-root': { maxWidth: 450 } }}
                />
            )}

            {/* Auto-shortlist confirm */}
            {autoShortlistConfirmDialog && (
                <DeleteConfirmationDialog
                    open={autoShortlistConfirmDialog}
                    handleClose={() => setAutoShortlistConfirmDialog(false)}
                    handleSubmission={async () => {
                        const nextEnabled = pendingAutoShortlistState;
                        const previousEnabled = autoShortlistEnabled;
                        // Optimistic: rolled back on failure.
                        setAutoShortlistEnabled(nextEnabled);
                        // Straight off the board's loaded columns - no extra request.
                        // null when the board has not reported yet, which reads as "unknown"
                        // below and shows the snackbar rather than assuming nothing to do.
                        const invitedStatusId = statusesData?.find((s) => s?.mappedValue === 'invited')?.value;
                        const invitedColumn = (boardColumns || []).find((c) => String(c?._id) === String(invitedStatusId));
                        const invitedCount = invitedColumn ? Number(invitedColumn?.count ?? 0) : null;
                        try {
                            await toggleAutoShortlistAsync({ investor_id, enabled: nextEnabled });
                            queryClient.invalidateQueries([`master-listing-template-all-investor-types-fetch-all/deal_flow${investor_id}`]);
                            // Closing and showing in one tick: React batches them into a single
                            // render, so the dialog leaves as the snackbar arrives.
                            setAutoShortlistConfirmDialog(false);
                            if (!nextEnabled) {
                                // Turning it off mid-run should drop the pending reload and its snackbar.
                                clearAutoShortlistTimer();
                                setIsAutoShortlisting(false);
                                if (autoShortlistBatchRef.current) {
                                    removeBatch(autoShortlistBatchRef.current);
                                    autoShortlistBatchRef.current = null;
                                }
                                addToast('Auto-Shortlist disabled', 'success');
                            } else if (invitedCount === 0) {
                                // Nothing queued, so there is no progress to show.
                                addToast('Auto-Shortlist enabled', 'success');
                            } else {
                                // No reload here - the hook reloads once when the queue settles.
                                startAutoShortlistWithSnackbar(fetchedSettingFromBackend?.auto_shortlist?.delay_ms);
                            }
                        } catch (err) {
                            console.error('Auto-shortlist error:', err);
                            setAutoShortlistEnabled(previousEnabled);
                            setAutoShortlistConfirmDialog(false);
                            addToast('Error updating auto-shortlist setting', 'error');
                        }
                    }}
                    heading={pendingAutoShortlistState ? 'Enable Auto-Shortlist?' : 'Disable Auto-Shortlist?'}
                    subheading={pendingAutoShortlistState
                        ? 'New startups matching your Investment Thesis will be shortlisted automatically.'
                        : 'New startups will no longer be shortlisted automatically.'}
                    ActionText={pendingAutoShortlistState ? 'Enable' : 'Disable'}
                    CancelText="Cancel"
                    maxWidth="xs"
                    sx={{ '& > .MuiDialog-container > .MuiPaper-root': { maxWidth: 450, borderRadius: '20px' } }}
                />
            )}
        </ThemeBGWrapper>
    );
};

export default DealFlowBoard;
